import { ApiError, NetworkError, type ProblemDetails } from "./errors"

export const API_BASE = "/api/v1"

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE"

export interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  token?: string | null
  signal?: AbortSignal
  /** Sent as `Idempotency-Key` so a retried/double-clicked command runs once. */
  idempotencyKey?: string
  /** A non-JSON body sent as-is (file uploads). Its type becomes the Content-Type. */
  raw?: Blob
  /** Return the response body as a Blob (audio, images) instead of parsing JSON. */
  blob?: boolean
}

/** Low-level request without auth retry logic. Used by the session module and by `api()`. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, token, signal, idempotencyKey, raw } = options
  const method = options.method ?? (body === undefined && raw === undefined ? "GET" : "POST")

  const headers: Record<string, string> = { Accept: "application/json" }
  if (raw !== undefined) headers["Content-Type"] = raw.type || "application/octet-stream"
  else if (body !== undefined) headers["Content-Type"] = "application/json"
  if (token) headers.Authorization = `Bearer ${token}`
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey

  let response: Response
  try {
    response = await fetch(API_BASE + path, {
      method,
      headers,
      body: raw ?? (body === undefined ? undefined : JSON.stringify(body)),
      credentials: "same-origin",
      signal,
    })
  } catch (error) {
    if (signal?.aborted) throw error
    throw new NetworkError(error)
  }

  return parseResponse<T>(response, options.blob === true)
}

async function parseResponse<T>(response: Response, blob = false): Promise<T> {
  if (response.status === 204) return undefined as T
  if (blob && response.ok) return (await response.blob()) as T

  const isJson = (response.headers.get("Content-Type") ?? "").includes("json")
  const payload: unknown = isJson ? await response.json().catch(() => undefined) : undefined

  if (!response.ok) {
    const problem: ProblemDetails =
      payload && typeof payload === "object"
        ? { ...(payload as Partial<ProblemDetails>), status: response.status }
        : { status: response.status, title: response.statusText }
    throw new ApiError(problem)
  }

  return payload as T
}
